/*jshint esversion: 11 */

import submitData from "../../lib/SubmitData";
import submitDataToParent from "../../lib/SubmitDataToParent";
import Dialog from "../../component/dialog/Dialog";
import { elem, taskId, urlContext } from "../../config";
import localforage from "localforage";
import credentialsMemory from "../../service/CredentialsMemory.js";
import { createCredentialsContext } from "../../service/CredentialsService.js";
import "./SendViewComponent";
import "./SendComponent";

const PARTIAL_UPLOAD_STATUS = 422;

const parsePartialUploadResponse = async ( response ) => {
    if ( response?.status !== PARTIAL_UPLOAD_STATUS || typeof response.json !== "function" ) {
        return null;
    }

    try {
        const payload = await response.json();
        const failedNames = Array.isArray( payload?.failed )
            ? payload.failed
                .map( ( item ) => item?.name )
                .filter( Boolean )
            : [];

        return failedNames.length > 0 ? failedNames : null;
    } catch ( error ) {
        return null;
    }
};

const filterImagesByName = ( images = [], imageNames = [] ) => {
    const imageNameSet = new Set( imageNames );
    return images.filter( ( image ) => imageNameSet.has( image?.imageName ) );
};


async function SendView(configuration) {
    window.scrollTo(0, 0);
    const result = await localforage.getItem(taskId);
    const { env, withAuthorization } = result.config;
    const { translates, backgroundImage, component, topVisible = true, logoVisible = true, waveVisible = true } = configuration;
    const { images, additionalSteps, steps, model, config } = result;
    const isInIframe = window.self !== window.top;
    const requiresAuthorization = !isInIframe && withAuthorization !== undefined;
    const sendViewComponent = document.createElement(component);
    let storageAvailable = false;
    let context = null;
    let savedCredentials = false;
    if (requiresAuthorization && globalThis.crypto?.subtle) {
        try {
            context = await createCredentialsContext({ ...urlContext, config });
            credentialsMemory.initialize(context);
            savedCredentials = await credentialsMemory.hasSavedCredentials();
            storageAvailable = true;
        } catch {
            // Login without remembering remains available if encrypted storage cannot be used.
        }
    }
    credentialsMemory.initialize(context);
    sendViewComponent.i18n = { ...translates };
    sendViewComponent.backgroundImage = backgroundImage;
    sendViewComponent.authRequired = requiresAuthorization;
    sendViewComponent.btnEnabled = !requiresAuthorization;
    sendViewComponent.savedCredentials = savedCredentials;
    sendViewComponent.rememberAvailable = storageAvailable && !isInIframe;
    sendViewComponent.isInIframe = isInIframe;
    sendViewComponent.config = { ...config, env, logo: result.config?.logoUrl,
        logoVisible, topVisible, waveVisible, withAuthorization };
    sendViewComponent.authorize = async ({ mode, credentials, pin }) => {
        if (mode === "pin") {
            await credentialsMemory.unlockWithPin(pin);
        } else {
            if (pin !== null && !sendViewComponent.rememberAvailable) throw new Error("storageUnavailable");
            await credentialsMemory.useCredentials(credentials, pin);
        }
        sendViewComponent.loginError = false;
        sendViewComponent.authRequired = false;
        sendViewComponent.btnEnabled = true;
    };
    elem.innerHTML = "";
    elem.appendChild(sendViewComponent);

    return new Promise((resolve) => {
        let sending = false;
        const controller = new AbortController();
        const reauthorize = (loginError = false) => {
            credentialsMemory.clearMemory();
            sendViewComponent.loginError = loginError;
            sendViewComponent.authRequired = true;
            sendViewComponent.btnEnabled = false;
        };
        const showError = () => {
            sendViewComponent.btnEnabled = true;
            Dialog({ title: translates?.errorTitle, message: translates?.errorMessage, timeout: 4500 });
        };
        window.addEventListener("pageshow", (event) => {
            if (event.persisted && requiresAuthorization) reauthorize();
        }, { signal: controller.signal });
        sendViewComponent.addEventListener("send-resolved", async () => {
            if (sending) return;
            if (requiresAuthorization && !credentialsMemory.getCredentials()) {
                reauthorize();
                return;
            }
            sending = true;
            sendViewComponent.btnEnabled = false;
            try {
                const submitFunction = isInIframe ? submitDataToParent : submitData;
                let response = await submitFunction({ images, additionalSteps, steps, model });
                const failedImageNames = !isInIframe ? await parsePartialUploadResponse(response) : null;
                if (failedImageNames) {
                    const failedImages = filterImagesByName(images, failedImageNames);
                    if (failedImages.length > 0) {
                        response = await submitFunction({ images: failedImages, additionalSteps, steps, model });
                    }
                }
                if (response.status === 200) {
                    credentialsMemory.clearMemory();
                    controller.abort();
                    resolve();
                } else if ((response.status === 401 || response.status === 403 || response.status === 500) &&
                    requiresAuthorization && !isInIframe) {
                    reauthorize(true);
                } else {
                    showError();
                }
            } catch {
                showError();
            } finally {
                sending = false;
            }
        }, { signal: controller.signal });
    });
}

export default SendView;
